import {
  Autocomplete,
  AutocompleteChangeDetails,
  AutocompleteInputChangeReason,
  createFilterOptions,
  FormHelperText,
  InputLabel,
  Paper,
  TextField,
} from '@mui/material'
import React, { useEffect, useState } from 'react'
import { getI18n, useTranslation } from 'react-i18next'
import { useStore } from '../../Provider'
import '../styles/combobox.scss'
import { Cross, DropdownArrow, DropDownArrowFocused, DropdownArrow_blue } from '../styles/Icons'

interface ComboboxProps {
  curState?: any[] | any
  optionList: any[]
  label: string
  name?: string
  multiple?: boolean
  ariaLabel?: string
  dataTestId?: string
  disabled?: boolean
  disableCloseOnSelect?: boolean
  placeholder?: string
  onChange: (e: React.SyntheticEvent, v: string[] | string | null | any) => void
  onDelete?: (option: any) => void
  freeSolo?: boolean
  error?: boolean
  helperText?: string
  manInput?: boolean
  disableClearable?: boolean
  isAddable?: boolean
  bold?: boolean
  onInputChange?: (e: React.SyntheticEvent, v: string, reason?: AutocompleteInputChangeReason) => void
  groupBy?: (option: any) => string
}

interface filterItem {
  inputValue: string
  text: string
}

export const OPSkillsCombobox = (props: ComboboxProps): JSX.Element => {
  const { t } = useTranslation()

  const {
    curState,
    multiple,
    disableCloseOnSelect = multiple,
    optionList,
    label,
    ariaLabel,
    dataTestId,
    disabled,
    placeholder,
    onChange,
    onDelete,
    freeSolo,
    name,
    error,
    helperText,
    manInput,
    disableClearable = true,
    isAddable = false,
    bold,
    groupBy,
  } = props

  const rootStore = useStore()
  const { isAdmin } = rootStore.loginStore

  const filter = createFilterOptions<filterItem>()

  const [isOpen, setIsOpen] = useState(false)
  const [hover, setHover] = useState(false)
  const [itemValue, setItemValue] = useState<string[] | string | null>(multiple ? (curState ? curState : []) : curState ? curState : null)

  // Sync internal state if parent provides/changes curState
  useEffect(() => {
    if (typeof curState !== 'undefined') {
      setItemValue(multiple ? curState ?? [] : curState ?? null)
    }
  }, [curState, multiple])

  const handleChange = (e: React.SyntheticEvent, v: any, situation: string, option: AutocompleteChangeDetails<any> | undefined) => {
    if (situation === 'clear') {
      setItemValue(multiple ? [] : null)
      onDelete?.(option)
      onChange(e, multiple ? [] : null)
      return
    }
    if (situation === 'removeOption') {
      onDelete?.(option)
      return
    }
    setItemValue(v)
    onChange(e, v?.inputValue ?? v)
  }
  const handleManualInput = (e: React.SyntheticEvent, v: string, reason?: AutocompleteInputChangeReason) => {
    // Only freeSolo accepts typed text as the value. Otherwise MUI owns the input text and the value
    // must stay one of the options. 'reset' just mirrors the selected option's label, so it is skipped.
    // When multiple=true, Autocomplete.value must be an array.
    if (freeSolo && !multiple && reason === 'input') {
      setItemValue(v)
    }

    if (manInput && reason !== 'clear') {
      onChange(e, v)
    } else if (!multiple && reason === 'clear') {
      // Only forward clear events from manual input in single-select mode
      onDelete?.(v)
    }
  }

  const partiallyBold = (text: string) => {
    const translation = t('Buttons.AddNew')
    if (text.startsWith(translation)) {
      const plainText = text.substring(translation.length)
      return (
        <>
          <b>{translation}</b>
          {plainText}
        </>
      )
    } else {
      return <>{text}</>
    }
  }

  return (
    <>
      <InputLabel className='comboBoxLabel' error={error} disabled={disabled} id={label}>
        {label}
      </InputLabel>
      <Autocomplete
        filterOptions={(options, params) => {
          const filtered = filter(options, params)
          if (isAdmin && params.inputValue !== '' && isAddable && !optionList?.find((option) => option.text === params.inputValue)) {
            filtered.push({
              inputValue: params.inputValue,
              text: `${t('Buttons.AddNew')}: "${params.inputValue}"`,
            })
          }
          return filtered
        }}
        className={`combobox ${isOpen ? 'open' : ''} ${multiple && itemValue && itemValue.length > 0 ? 'multiOptions' : ''}`}
        multiple={multiple}
        freeSolo={freeSolo}
        forcePopupIcon
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        disableCloseOnSelect={disableCloseOnSelect}
        disabled={disabled}
        onChange={handleChange}
        value={multiple ? (Array.isArray(itemValue) ? itemValue : []) : itemValue} // ensure array in multiple mode
        selectOnFocus={false}
        options={optionList}
        disableClearable={disableClearable}
        aria-label={ariaLabel}
        data-testid={dataTestId}
        onOpen={() => setIsOpen(true)}
        onClose={() => setIsOpen(false)}
        groupBy={groupBy}
        renderGroup={
          groupBy
            ? (params) => (
                <li key={params.key} className='MuiAutocomplete-group'>
                  <div className='MuiAutocomplete-groupLabel comboGroupLabel'>{params.group}</div>
                  <ul className='MuiAutocomplete-groupUl comboGroupUl'>{params.children}</ul>
                </li>
              )
            : undefined
        }
        clearIcon={<Cross onMouseDown={(e) => e.stopPropagation()} />}
        popupIcon={isOpen ? <DropDownArrowFocused /> : hover ? <DropdownArrow_blue /> : <DropdownArrow />}
        ChipProps={{
          className: 'chipStyling',
          deleteIcon: <Cross />,
        }}
        renderOption={(props, option) => {
          return (
            <li {...props} key={option.key || option.text}>
              {bold
                ? partiallyBold(getI18n().language === 'en' && option.textEng && option.textEng.length ? option.textEng : option.text)
                : getI18n().language === 'en' && option.textEng && option.textEng.length
                ? option.textEng
                : option.text}
            </li>
          )
        }}
        isOptionEqualToValue={(option, value) => {
          if (!option || !value) return false
          if (typeof option === 'string' || typeof value === 'string') return option === value
          return option.key === value.key
        }}
        getOptionLabel={(option) => {
          if (typeof option === 'string') {
            return option
          }
          if (option.inputValue) {
            return option.inputValue
          }
          return getI18n().language === 'en' && option.textEng && option.textEng.length ? option.textEng : option.text
        }}
        PaperComponent={({ children }) => <Paper className='paperStyle'>{children}</Paper>}
        renderInput={(params) => (
          <TextField
            error={error}
            name={name}
            {...params}
            InputProps={{ ...params.InputProps, disableUnderline: true }}
            variant='standard'
            placeholder={placeholder}
          />
        )}
        onInputChange={handleManualInput}
      />
      {error && !disabled && <FormHelperText error>{helperText}</FormHelperText>}
    </>
  )
}
